import axios, { AxiosError } from "axios";
import { ConsentAction, getStorageMechanism, } from "@finarkein/aasdk-core";
import { getMappedLinkedAccounts, getConsentRequestDetailsMapped, handleErrors, isFIPAvailable, } from "./operations";
import { ResponseMapperProtean } from "./utils/ResponseMapperProtean";
import { OPEN_URLS, Routes } from "./utils/RouteConstants";
export class ProteanService {
    constructor() {
        this.availableStorage = getStorageMechanism();
        this.timeout = 45000;
        this.noOfRetries = 5;
        this.delayTimeout = 3000;
        this.discoverySignatureMap = new Map();
        this.sendRawResponse = false;
        this.listOfFips = [];
        this.identifier = () => "UATNSDLAA";
    }
    setup(initOptions) {
        this.baseUrl = initOptions.url;
        if (initOptions.rawResponse) {
            this.sendRawResponse = true;
        }
        if (initOptions.timeout) {
            this.timeout = +initOptions.timeout;
        }
        if (initOptions.count_of_retries) {
            this.noOfRetries = +initOptions.count_of_retries;
        }
        if (initOptions.delay_between_retries) {
            this.delayTimeout = +initOptions.delay_between_retries;
        }
        this.http = axios.create({
            baseURL: initOptions.url,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            timeout: this.timeout,
            withCredentials: false,
        });
        this.http.interceptors.request.use(async (config) => {
            const headers = config.headers;
            const skipAccessToken = OPEN_URLS.some(url => { var _a; return (_a = config.url) === null || _a === void 0 ? void 0 : _a.includes(url); });
            headers.set("Content-Type", "application/json");
            if (!skipAccessToken) {
                headers.setAuthorization(`Bearer ${this.resolveAccessToken()}`);
            }
            return config;
        }, (error) => Promise.reject(error));
        this.http.interceptors.response.use((response) => {
            return response;
        }, async (error) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const originalRequest = error.config;
            // token expired
            if ((((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && !originalRequest._retry) || (((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 400 && ((_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.code) === 5000)) {
                originalRequest._retry = true;
                const refreshToken = this.availableStorage.getValue("REFRESH-TOKEN");
                const httpForAccessToken = axios.create({
                    baseURL: initOptions.url,
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    timeout: 50000,
                    withCredentials: false,
                });
                httpForAccessToken.interceptors.request.use((config) => {
                    return config;
                }, (error) => Promise.reject(error));
                try {
                    const response = await (httpForAccessToken === null || httpForAccessToken === void 0 ? void 0 : httpForAccessToken.post(`${initOptions.url}${Routes.REFRESH_TOKEN}`, { refreshToken }));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                        const accessToken = response.data["access_token"];
                        this.availableStorage.setValue("ACCESS-TOKEN", accessToken);
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        return axios(originalRequest);
                    }
                }
                catch (error) {
                    if (((_e = error === null || error === void 0 ? void 0 : error.response) === null || _e === void 0 ? void 0 : _e.status) === 500) {
                        return {
                            message: "Session Timed Out. Please restart the journey",
                            status: "SESSION_EXPIRED",
                            errorCode: ((_g = (_f = error === null || error === void 0 ? void 0 : error.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.code) || "",
                        };
                    }
                    else {
                        return {
                            message: "Some unknown error occurred",
                            status: "FAILURE",
                            errorCode: ((_j = (_h = error === null || error === void 0 ? void 0 : error.response) === null || _h === void 0 ? void 0 : _h.data) === null || _j === void 0 ? void 0 : _j.code) || "",
                        };
                    }
                }
            }
            return Promise.reject(error);
        });
        return new Promise((resolve) => resolve());
    }
    resolveAccessToken() {
        return this.availableStorage.getValue("ACCESS-TOKEN")
            ? this.availableStorage.getValue("ACCESS-TOKEN")
            : null;
    }
    async initiateLogin(input) {
        const request = {
            phoneNumber: input.mobileNum,
            isResetPin: false
        };
        let retryCount = 0;
        const attemptRequest = () => {
            var _a;
            return (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.SEND_LOGIN_OTP}`, request).then((response) => {
                if (response.status === 200) {
                    this.availableStorage.setValue("userId", request.phoneNumber);
                    this.availableStorage.setValue("isUserRegistered", true);
                    return {
                        statusCode: response.status,
                        status: "SEND",
                        otpReference: response.data,
                    };
                }
            }).catch(async (error) => {
                var _a;
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
                    return this.initiateSignup(input);
                }
                return handleErrors(error, "VALIDATE_USER");
            });
        };
        return attemptRequest();
    }
    async initiateSignup(input) {
        const request = {
            phoneNumber: input.mobileNum,
            isResetPin: false
        };
        let retryCount = 0;
        const attemptRequest = () => {
            var _a;
            return (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.SEND_SIGNUP_OTP}`, request).then((response) => {
                this.availableStorage.setValue("userId", request.phoneNumber);
                this.availableStorage.setValue("isUserRegistered", false);
                return {
                    statusCode: response.status,
                    status: "SEND",
                    otpReference: response.data,
                };
            }).catch(async (error) => {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    handleErrors(error, "VALIDATE_USER");
                }
            });
        };
        return attemptRequest();
    }
    async authInit(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            let aaOperation = undefined;
            let promise;
            try {
                if (input.mobileNum) {
                    aaOperation = "PROTEAN_AUTH_MOBILE_CUSTOMERS-FIU-LOGIN";
                    promise = this.initiateLogin(input);
                }
                else {
                    aaOperation = "PROTEAN_AUTH_WEBVIEW_CUSTOMERS-FIU-VALIDATE";
                    const request = {
                        fi: input.encryptedFiuId,
                        ecreq: input.encryptedRequest,
                        reqdate: input.requestDate,
                    };
                    promise = (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.DECODE}`, request).then(async (response) => {
                        const data = response.data;
                        this.availableStorage.setValue("userId", data.phoneNumber);
                        this.availableStorage.setValue("isUserRegistered", data.isUserPresent);
                        return await data.isUserPresent ? this.initiateLogin({ mobileNum: data.phoneNumber }) : this.initiateSignup({ mobileNum: data.phoneNumber });
                    });
                }
                return await promise;
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, aaOperation); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("VALIDATE_USER");
    }
    async authVerify(input) {
        const login = Routes.VERIFY_OTP;
        const register = Routes.SIGNUP_VERIFY_OTP;
        let finalUrl = "";
        let type = "";
        let customerReq = {};
        const isUserRegistered = this.availableStorage.getValue("isUserRegistered");
        if (isUserRegistered === "true" || isUserRegistered === true) {
            customerReq = {
                phone_number: this.availableStorage.getValue("userId"),
                otpUniqueID: input.otpReference,
                code: input.otp,
                grant_type: "otp",
                isResetPin: false,
                isTermsAndConditionAgreed: true
            };
            finalUrl = login;
            type = "AA_LOGIN";
        }
        else {
            customerReq = {
                phoneNumber: this.availableStorage.getValue("userId"),
                otpUniqueID: input.otpReference,
                code: input.otp,
                isFromRedirection: true,
                isTermsAndConditionAgreed: true
            };
            finalUrl = register;
            type = "AA_REGISTER";
        }
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            const aaOperation = type === "AA_REGISTER"
                ? "PROTEAN_AUTH_CUSTOMERS-FIU-REGISTER"
                : "PROTEAN_AUTH_CUSTOMERS-FIU-LOGIN";
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${finalUrl}`, customerReq));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    //store customer id in session storage
                    const accessToken = response.data["access_token"];
                    const refreshToken = response.data["refresh_token"];
                    this.availableStorage.setValue("ACCESS-TOKEN", accessToken);
                    this.availableStorage.setValue("REFRESH-TOKEN", refreshToken);
                    this.availableStorage.setValue("customerId", response.data.phoneNumber);
                    this.availableStorage.setValue("sessionId", response.data.session_state);
                    return {
                        status: "SUCCESS",
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, aaOperation); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("AA_LOGIN");
    }
    async mobileAuthInit(input) {
        const req = {
            categoryType: "STRONG",
            type: "MOBILE",
            value: input.mobileNum
        };
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.IDENTIFIERS}`, req));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200 && ((_b = response.data.mobile) === null || _b === void 0 ? void 0 : _b.otpUniqueID)) {
                    this.availableStorage.setValue("mobileValidationId", response.data.mobile.otpUniqueID);
                    return {
                        status: "SEND",
                        id: response.data.mobile.otpUniqueID,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_AUTH_MOBILE-VALIDATIONS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("ALT_MOBILE");
    }
    async mobileAuthVerify(input) {
        const mobileValidationId = input.mobile_validation_id
            ? input.mobile_validation_id
            : this.availableStorage.getValue("mobileValidationId");
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const confirmRequest = {
                    code: input.otp,
                    identifierType: "MOBILE",
                    otpUniqueID: mobileValidationId,
                    phoneNumber: input.mobileNum
                };
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.IDENTIFIERS_VERIFY}`, confirmRequest));
                await this.getFIPList();
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        message: "Mobile Authentication Successful",
                        data: response.data,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_AUTH_MOBILE-VALIDATIONS-RESPONSE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("CONFIRM_ALT_MOBILE");
    }
    async getLinkedAccounts() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.LINKED_ACCOUNTS}`, { data: {} }));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        LinkedAccounts: getMappedLinkedAccounts(response.data),
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_LINKED-ACCOUNTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("LINKED_ACCOUNTS");
    }
    async discoverAccounts(input) {
        var _a, _b;
        let retryCount = 0;
        const ids = [];
        (_a = input.Customer) === null || _a === void 0 ? void 0 : _a.Identifiers.forEach((data) => {
            ids.push({
                type: data.type,
                value: data.value,
                categoryType: data.category
            });
        });
        const request = {
            FITypes: input.FITypes,
            FipId: (_b = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _b === void 0 ? void 0 : _b.fipId,
            Identifiers: ids,
        };
        const attemptRequest = async () => {
            var _a, _b, _c, _d;
            try {
                // If Fip is available with AA call discovery else return error
                if (isFIPAvailable(await this.getFIPList(), `${request.FipId}`)) {
                    const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.DISCOVER_ACCOUNTS}`, request));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                        this.discoverySignatureMap.set((_b = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _b === void 0 ? void 0 : _b.fipId, (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.signature);
                        return {
                            status: "SUCCESS",
                            DiscoveredAccounts: (_d = response === null || response === void 0 ? void 0 : response.data) === null || _d === void 0 ? void 0 : _d.DiscoveredAccounts,
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                return {
                    status: "ERROR",
                    errorCode: "FIP_UNAVAILABLE",
                    errorMessage: "Financial Institution is not available",
                    statusCode: 404,
                };
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_ACCOUNTS-DISCOVER"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("ACCOUNT_DISCOVERY");
    }
    async linkAccounts(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b, _c, _d;
            try {
                const request = {
                    FipId: (_a = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _a === void 0 ? void 0 : _a.fipId,
                    Accounts: (_b = input === null || input === void 0 ? void 0 : input.Customer) === null || _b === void 0 ? void 0 : _b.Accounts,
                    signature: this.discoverySignatureMap.get((_c = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _c === void 0 ? void 0 : _c.fipId)
                };
                this.availableStorage.setValue("linkingFip", request.FipId);
                const response = await ((_d = this.http) === null || _d === void 0 ? void 0 : _d.post(`${this.baseUrl}${Routes.LINK_ACCOUNTS}`, request));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        AuthenticatorType: response.data.authenticatorType,
                        RefNumber: response.data.refNumber,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_ACCOUNTS-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("ACCOUNT_LINKING");
    }
    async confirmLinking(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const fipId = this.availableStorage.getValue("linkingFip");
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/User/account/link/${input.AccountsLinkingRefNumber}/${input.token}?FipId=${fipId}`, { data: {} }));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    this.availableStorage.removeValue("linkingFip");
                    return {
                        status: "SUCCESS",
                        AccLinkDetails: response.data.AccLinkDetails,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_ACCOUNTS-CONFIRM-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("CONFIRM_ACCOUNT_LINKING");
    }
    async consentRequestDetails(consentHandle) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.CONSENT_REQUEST_DETAILS}/${consentHandle}`, { data: {} }));
                const data = (response === null || response === void 0 ? void 0 : response.data) || {};
                return {
                    ...getConsentRequestDetailsMapped(data),
                    statusCode: response === null || response === void 0 ? void 0 : response.status
                };
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_GET_CONSENT-REQUESTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("CONSENT_DETAILS");
    }
    async consent(input) {
        let retryCount = 0;
        const type = input.handleStatus === ConsentAction.ACCEPT
            ? "CONSENT_ACTION_APPROVE"
            : "CONSENT_ACTION_REJECT";
        const accounts = input.FIPDetails.flatMap(obj => obj.Accounts.map(account => ({
            fipHandle: obj.FIP.id,
            linkRefNumber: account.linkRefNumber
        })));
        const attemptRequest = async () => {
            var _a, _b, _c, _d;
            try {
                if (input.handleStatus === ConsentAction.ACCEPT) {
                    const initResponse = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/User/Consent/Approval/${input.consentHandleId}/APPROVED`, {}));
                    if ((initResponse === null || initResponse === void 0 ? void 0 : initResponse.status) === 200) {
                        // FIXME: Hardcoded otp can be removed once protean team allow consent approval without otp
                        const request = {
                            "authType": "OTP",
                            "authValue": "123456",
                            consentHandle: input.consentHandleId,
                            accounts,
                            // Consent spelling is wrong from AA side
                            constentApprovalStatus: "APPROVED",
                            otpUniqueID: initResponse.data.otpUniqueID,
                        };
                        const response = await ((_b = this.http) === null || _b === void 0 ? void 0 : _b.post(`${this.baseUrl}${Routes.CONSENT_APPROVAL}`, request));
                        return {
                            status: "SUCCESS",
                            message: "Consent Stored",
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                else {
                    const initResponse = await ((_c = this.http) === null || _c === void 0 ? void 0 : _c.post(`${this.baseUrl}/User/Consent/Approval/${input.consentHandleId}/REJECTED`, {}));
                    if ((initResponse === null || initResponse === void 0 ? void 0 : initResponse.status) === 200) {
                        // FIXME: Hardcoded otp can be removed once protean team allow consent approval without otp
                        const rejectReq = {
                            "authType": "OTP",
                            "authValue": "123456",
                            consentHandle: input.consentHandleId,
                            accounts,
                            constentApprovalStatus: "REJECTED",
                            otpUniqueID: initResponse.data.otpUniqueID,
                        };
                        const response = await ((_d = this.http) === null || _d === void 0 ? void 0 : _d.post(`${this.baseUrl}${Routes.CONSENT_APPROVAL}`, rejectReq));
                        if ((response === null || response === void 0 ? void 0 : response.status) === 200 || (response === null || response === void 0 ? void 0 : response.status) === 201) {
                            return {
                                status: "SUCCESS",
                                message: "Consent Stored",
                                statusCode: response === null || response === void 0 ? void 0 : response.status,
                            };
                        }
                    }
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_" + type); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("CONSENT_ACTION");
    }
    async logout() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const sessionId = this.availableStorage.getValue("sessionId");
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.SIGN_OUT}?sessionId=${sessionId}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    this.cleanUp();
                    return {
                        statusCode: response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_INDIVIDUAL_LOGOUT"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("LOGOUT");
    }
    async getUserInfo() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.USER_INFO}`, { data: {} }));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        UserInfo: {
                            mobileNo: response.data.mobileNumber,
                            userId: response.data.vuaId,
                        },
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "PROTEAN_INDIVIDUAL_PROFILE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperProtean(attemptRequest()).getResponse("USER_INFO");
    }
    cleanUp() {
        var _a;
        this.discoverySignatureMap = new Map();
        if (this.availableStorage) {
            (_a = this.availableStorage) === null || _a === void 0 ? void 0 : _a.removeAllValues();
        }
    }
    async getFIPList() {
        var _a;
        try {
            if (Array.isArray(this.listOfFips) && this.listOfFips.length > 0) {
                return this.listOfFips;
            }
            const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.FIP_LIST}`, { data: {} }));
            const { data, status } = response || {};
            if (status === 200 && Array.isArray(data)) {
                this.listOfFips = data.filter((fip) => fip.isEnabled);
            }
            return this.listOfFips;
        }
        catch (error) {
            console.error(error);
            return [];
        }
    }
}
//# sourceMappingURL=aasdk-protean.js.map